import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export const TEAL = '#00798E';
export const TEAL_LIGHT = 'rgba(0, 121, 142, 0.07)';
export const TEAL_BORDER = 'rgba(0, 121, 142, 0.28)';

export const STATUS_LIST = ['SCHEDULED', 'CHECKED_IN', 'ATTENDED', 'COMPLETED', 'CANCELLED'];

export const STATUS_LABELS = {
  SCHEDULED: 'Scheduled',
  CHECKED_IN: 'Checked-In',
  ATTENDED: 'Attended',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

// Literal hex values (not just MUI chip-color names) — needed for calendar
// event backgrounds and kanban column accents. Mirrors the STATUS_HEX
// pattern used in layouts/followUps/index.js.
export const STATUS_HEX = {
  SCHEDULED: '#0288d1',
  CHECKED_IN: '#ed6c02',
  ATTENDED: '#7b1fa2',
  COMPLETED: '#2e7d32',
  CANCELLED: '#d32f2f',
};

export const STATUS_CHIP_COLOR = {
  SCHEDULED: 'info',
  CHECKED_IN: 'warning',
  ATTENDED: 'secondary',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

// From-state -> allowed to-states. Mirrors ALLOWED_TRANSITIONS in
// apps/opd/src/services/appointment.service.ts — kept in sync manually.
// Used client-side only to block obviously-invalid drags/actions before
// the confirm dialog even opens; the server is still the source of truth.
// SCHEDULED -> ATTENDED is allowed here even though the server's own
// ALLOWED_TRANSITIONS map doesn't list it directly — that drag calls
// markAttendedDirect() (attend-direct endpoint), which still performs a
// real CHECKED_IN transition first, just in the same request.
export const ALLOWED_TRANSITIONS = {
  SCHEDULED: ['CHECKED_IN', 'ATTENDED', 'CANCELLED'],
  CHECKED_IN: ['ATTENDED', 'CANCELLED'],
  ATTENDED: ['COMPLETED', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

// The suggested lifecycle, surfaced to the user via LifecycleLegend.
export const LIFECYCLE_STEPS = ['SCHEDULED', 'CHECKED_IN', 'ATTENDED', 'COMPLETED'];

export const KANBAN_COLUMNS = [
  { status: 'SCHEDULED', label: 'Scheduled' },
  { status: 'CHECKED_IN', label: 'Checked-In' },
  { status: 'ATTENDED', label: 'Attended' },
  { status: 'COMPLETED', label: 'Completed' },
  { status: 'CANCELLED', label: 'Cancelled' },
];

export function getCurrentUserId() {
  try {
    const raw = localStorage.getItem('user') || localStorage.getItem('userData');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.user_id || parsed?.id || 1;
    }
  } catch {
    /* ignore parse errors */
  }
  return 1;
}

// Shown next to the status chip wherever an appointment is rendered (drawer,
// list, calendar, kanban, doctor-serial) for is_guest === 'Y' rows.
export const GUEST_CHIP_LABEL = 'Guest';
export const GUEST_CHIP_COLOR = '#8d6e63';

export function resolvePatientName(appointment, contactId) {
  if (appointment?.is_guest === 'Y') {
    return appointment.guest_name || `Guest #${appointment.id ?? contactId}`;
  }
  const contactInfo = appointment?.contactInfo ?? appointment;
  if (!contactInfo) return `Patient #${contactId}`;
  const c = contactInfo.contact || contactInfo;
  return (
    c?.full_name ||
    [c?.first_name, c?.middle_name, c?.last_name].filter(Boolean).join(' ') ||
    `Patient #${contactId}`
  );
}

export function resolveDoctorName(doctorInfo, doctorId) {
  return doctorInfo?.name || doctorInfo?.user_name || doctorInfo?.full_name || `Doctor #${doctorId}`;
}

// appointment.contactInfo is the "detailedContact" shape from
// v1:get-patient-by-contact-id — carries a `phones` array (same as
// followUps/index.js's resolvePhone). Prefers the default mobile number,
// falls back to any mobile, then any phone. For a guest appointment there's
// no contact yet — read the mobile captured at booking instead.
export function resolvePatientMobile(appointment) {
  if (appointment?.is_guest === 'Y') {
    return appointment.guest_mobile
      ? `${appointment.guest_country_code || ''}${appointment.guest_mobile}`
      : null;
  }
  const contactInfo = appointment?.contactInfo ?? appointment;
  const c = contactInfo?.contact || contactInfo;
  const phones = c?.phones || [];
  const phone =
    phones.find(p => p.phone_type === 'mobile' && p.is_default === 'Y') ||
    phones.find(p => p.phone_type === 'mobile') ||
    phones[0];
  return phone?.phone_number || null;
}

export function formatAppointmentNo(appointment) {
  return appointment?.appointment_no || `#${appointment?.id ?? ''}`;
}

export const TIME_PICKER_STEP_MINUTES = 5;

// MDTimePicker (components/MDTimePicker) hands back "hh:mm A" (12-hour,
// AM/PM) — the backend's appointment_time column is a plain MySQL TIME, so
// it needs to travel over the API as 24-hour "HH:mm". Returns undefined for
// anything blank/unparseable so callers can `?? undefined` it straight into
// a request payload.
export function formatTimeFor24Hour(displayTime) {
  if (!displayTime) return undefined;
  const parsed = dayjs(displayTime, 'hh:mm A');
  return parsed.isValid() ? parsed.format('HH:mm') : undefined;
}

// Every date/time shown anywhere in the OPD Appointment module goes through
// these — dd/mm/yyyy for dates, 12-hour hh:mm AM/PM for times. Accepts
// whatever shape the value happens to already be in (backend 24-hour
// "HH:mm:ss"/"HH:mm" TIME strings, or an already-12-hour "hh:mm A" string
// picked up mid-edit from MDTimePicker) and normalises it.
export function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const parsed = dayjs(dateStr);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : dateStr;
}

export function formatDisplayTime(timeStr) {
  if (!timeStr) return '';
  const parsed = dayjs(timeStr, ['HH:mm:ss', 'HH:mm', 'hh:mm A'], true);
  return parsed.isValid() ? parsed.format('hh:mm A') : timeStr;
}

export function formatAppointmentDateTime(dateStr, timeStr) {
  return [formatDisplayDate(dateStr), formatDisplayTime(timeStr)].filter(Boolean).join(' ');
}

// For full timestamps (e.g. History log created_at) rather than a separate
// appointment_date + appointment_time pair.
export function formatFullDateTime(isoOrDate) {
  if (!isoOrDate) return '—';
  const parsed = dayjs(isoOrDate);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY hh:mm A') : '—';
}

const CM_PER_METER = 100;
const BMI_DECIMAL_PLACES = 1;

// weight(kg) / height(m)^2 — returns '' if either input isn't a usable positive number.
export function calculateBmi(weightKg, heightCm) {
  const weight = parseFloat(weightKg);
  const height = parseFloat(heightCm);
  if (!weight || !height || weight <= 0 || height <= 0) return '';
  const heightInMeters = height / CM_PER_METER;
  return (weight / (heightInMeters * heightInMeters)).toFixed(BMI_DECIMAL_PLACES);
}

// weight/height/bmi are stored as free-text (irm_prescriptions columns are
// VARCHAR) — the input field only ever holds the bare number (the unit is
// shown separately as an InputAdornment), so the unit must be appended here,
// once, right before the value is sent to the API. bp_systolic/bp_diastolic
// are real INTEGER columns and must stay bare numbers — never call this on them.
export function withUnit(value, unit) {
  if (value === undefined || value === null) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed) return undefined;
  return trimmed.toLowerCase().endsWith(unit.toLowerCase()) ? trimmed : `${trimmed} ${unit}`;
}

// Mirrors the URL built in layouts/prescriptionList/PatientPrescriptionSearch.js —
// same route, same "undefined" literal fallback when there's no couple record.
export function buildPrescriptionPrintUrl(appointment, prescriptionCode) {
  const patientCode = appointment?.contactInfo?.external_code;
  const coupleCode =
    appointment?.contactInfo?.patientCouple?.external_code ??
    appointment?.contactInfo?.spouseCouple?.external_code;
  return `/prescription/view/${patientCode}/${coupleCode || 'undefined'}/${prescriptionCode}`;
}

const DROPDOWN_MIN_HEIGHT = 36;

// Every Select/dropdown on this dashboard uses this — minimum 36px tall,
// prominent (bold, readable) selected-value text.
export const DROPDOWN_SX = {
  '& .MuiOutlinedInput-root': { minHeight: DROPDOWN_MIN_HEIGHT },
  '& .MuiSelect-select': {
    minHeight: `${DROPDOWN_MIN_HEIGHT}px !important`,
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '0.85rem',
    paddingTop: '6px',
    paddingBottom: '6px',
  },
};

const MAX_DATE_RANGE_MONTHS = 6;

// Default date-range for List/Board views: today only.
export function getDefaultDateRange() {
  const today = dayjsToday();
  return { start: today, end: today };
}

// Earliest allowed start date given a chosen end date — caps the range at 6 months.
export function getMinAllowedStartDate(endDate) {
  return shiftMonths(endDate, -MAX_DATE_RANGE_MONTHS);
}

function dayjsToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export const getTodayDateStr = dayjsToday;

function shiftMonths(dateStr, monthsDelta) {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + monthsDelta);
  return d.toISOString().slice(0, 10);
}

const IN_PROGRESS_STATUSES = ['CHECKED_IN', 'ATTENDED'];
const WAITING_STATUSES = ['SCHEDULED', 'CHECKED_IN'];

// Per-doctor live-queue snapshot for *today* only — "IN" is whoever the
// doctor currently has (ATTENDED takes priority over just CHECKED_IN),
// "NEXT" is the next patient still waiting, by serial order after IN.
// appointments must already be pre-filtered to one doctor + today.
export function getQueueSynopsis(appointmentsForDoctorToday) {
  const sorted = [...(appointmentsForDoctorToday || [])].sort((a, b) => a.serial_no - b.serial_no);
  const attended = sorted.find(a => a.status === 'ATTENDED');
  const checkedIn = sorted.filter(a => a.status === 'CHECKED_IN');
  const inAppointment = attended || checkedIn[0] || null;

  let nextAppointment = null;
  if (inAppointment) {
    nextAppointment = sorted.find(
      a => a.serial_no > inAppointment.serial_no && WAITING_STATUSES.includes(a.status),
    ) || null;
  } else {
    nextAppointment = sorted.find(a => WAITING_STATUSES.includes(a.status)) || null;
  }

  return { inAppointment, nextAppointment };
}

export function isQueueInProgress(status) {
  return IN_PROGRESS_STATUSES.includes(status);
}

// Groups a mixed list of appointments by doctor (irm_users_id), each group
// sorted by date then serial_no — used by the List view's doctor-wise layout.
export function groupAppointmentsByDoctor(appointments) {
  const groups = new Map();
  for (const appointment of appointments) {
    const key = appointment.irm_users_id;
    if (!groups.has(key)) {
      groups.set(key, { doctorId: key, doctorInfo: appointment.doctorInfo, appointments: [] });
    }
    groups.get(key).appointments.push(appointment);
  }
  const result = Array.from(groups.values());
  result.forEach(group => {
    group.appointments.sort((a, b) => {
      if (a.appointment_date !== b.appointment_date) {
        return a.appointment_date < b.appointment_date ? -1 : 1;
      }
      return a.serial_no - b.serial_no;
    });
  });
  result.sort((a, b) => resolveDoctorName(a.doctorInfo, a.doctorId).localeCompare(resolveDoctorName(b.doctorInfo, b.doctorId)));
  return result;
}
